import {
  inject,
  Injectable,
} from '@angular/core';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import {environment} from '../../../../../../../environments/environment';
import {Links} from '../../../../../common/constants/links';
import {Constants} from '../../../../../common/constants/constants';
import {UrlFunctions} from '../../../../../common/functions/url';

const ADMIN_INSURER_HASH_PREFIX = 'insurer-';

const ADMIN_INSURER_ACCOUNTS_TAB = 'accounts';

@Injectable({
  providedIn: 'root',
})
export class RedirectService {

  private router = inject(Router);

  static readonly FROM_SOBRADO_RETURN_TO_SNG_CRM = 1;

  static readonly FROM_SOBRADO_RETURN_TO_SNG_DASHBOARD = 2;

  /**
   * Method: Build the fromSng query parameter string (e.g. '?fromSng=1').
   * @param value - The fromSng value indicating the return destination.
   * @param separator - '?' when the url carries no query yet, '&' otherwise.
   */
  private buildFromSngParam(value: number, separator: string): string {
    return `${separator}${Constants.FROM_SNG_PARAM_KEY}${value}`;
  }

  /**
   * Build a Sobrado url keeping the fromSng parameter in front of the fragment.
   * @param request - The Sobrado path without host, optionally carrying a query and a '#fragment'.
   * @param value - The fromSng value indicating the return destination.
   */
  private buildSobradoUrlWithFromSng(request: string, value: number): string {
    const hashIndex = request.indexOf(Constants.HASHTAG);
    const target = hashIndex === -1 ? request : request.slice(0, hashIndex);
    const fragment = hashIndex === -1 ? '' : request.slice(hashIndex);
    const separator = target.includes(Constants.QUESTION_MARK) ? Constants.AMPERSAND : Constants.QUESTION_MARK;
    return `${this.getRedirectToSobrado()}${target}${this.buildFromSngParam(value, separator)}${fragment}`;
  }

  /**
   * Method: Redirect by request link
   * @param request
   * @param isReturnBackToSng
   * @param openInNewTab - Open the URL in a new tab; default false.
   */
  public redirectToSobradoLink(request: string, isReturnBackToSng?: boolean, openInNewTab = false): void {
    const finalUrl = this.getRedirectSobradoLink(request, isReturnBackToSng);
    if (openInNewTab) {
      this.windowOpenInNewTab(finalUrl);
    } else {
      window.location.href = finalUrl;
    }
  }

  /**
   * Method: Redirect to a Sobrado link with return-to-dashboard param.
   * @param request - The Sobrado path to redirect to.
   */
  public redirectToSobradoLinkWithReturnToDashboard(request: string): void {
    window.location.href = this.buildSobradoUrlWithFromSng(
      request,
      RedirectService.FROM_SOBRADO_RETURN_TO_SNG_DASHBOARD,
    );
  }

  /**
   * Method: Redirect by request link
   * @param request
   */
  public redirectToSngLink(request?: string): void {
    const server = environment.sngLink;
    window.location.href = server + request;
  }

  /**
   * Method: Get string with redirect link
   * @param request - The Sobrado path without host, optionally carrying a query and a '#fragment'.
   * @param isReturnBackToSng - Add the parameter that returns the user to the CRM 2.0 client.
   */
  public getRedirectSobradoLink(request: string, isReturnBackToSng = false): string {
    return isReturnBackToSng
      ? this.buildSobradoUrlWithFromSng(request, RedirectService.FROM_SOBRADO_RETURN_TO_SNG_CRM)
      : `${this.getRedirectToSobrado()}${request}`;
  }

  /**
   * Method: Get string with redirect to Sobrado
   */
  public getRedirectToSobrado(): string {
    return environment.redirectLinkUrl;
  }

  /**
   * Method: Get the Sobrado admin-insurers page link pre-filtered by insurer name
   * @param insurerName - Insurer name used as the search query
   */
  public getAdminInsurersSearchLink(insurerName: string): string {
    const query = encodeURIComponent(insurerName);
    return this.getRedirectSobradoLink(`${Links.LINK_ADMIN_INSURERS}${Constants.SEARCH_PARAM_KEY}${query}`);
  }

  /**
   * Method: Get the Sobrado admin-insurers link that opens the insurer edit dialog on the accounts tab
   * @param insurerId - Legacy insurer id
   */
  public getAdminInsurerAccountsLink(insurerId: number): string {
    const deepLink = `${Constants.HASHTAG}${ADMIN_INSURER_HASH_PREFIX}${insurerId}`
      + `${Constants.SLASH}${ADMIN_INSURER_ACCOUNTS_TAB}`;
    return this.getRedirectSobradoLink(`${Links.LINK_ADMIN_INSURERS}${deepLink}`);
  }

  /**
   * Method: Get string url for redirect to login page
   */
  public getRedirectToLogin(): string {
    return this.getRedirectSobradoLink(Links.LINK_AUTH_LOGIN);
  }

  /**
   * Method: Get string url for redirect to getmorebrain page
   */
  public getRedirectToGetMoreBrain(): string {
    return environment.getMoreBrainUrl;
  }

  /**
   * Method: Window open in new tab
   * @param url
   */
  public windowOpenInNewTab(url: string): void {
    if (!url) {
      return;
    }
    window.open(url, '_blank');
  }

  /**
   * Method: open link in browser tab
   * @param activatedRoute
   * @param relativeLink
   */
  public openInNewTab(activatedRoute: ActivatedRoute, relativeLink: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([relativeLink], {relativeTo: activatedRoute}));
    this.windowOpenInNewTab(url);
  }

  /**
   * Method: Redirect after oidc auth
   * @param url - url to redirect to
   */
  public async redirectAfterOidcAuth(url: string): Promise<void> {
    if (UrlFunctions.hasQueryParams(url)) {
      await this.router.navigate(
        [UrlFunctions.getUrlWithoutQueryParams(url)],
        {
          queryParams: UrlFunctions.getQueryParamsFromUrl(url),
          replaceUrl: true,
        },
      );
    } else {
      await this.router.navigate([url], {replaceUrl: true});
    }
  }

  /**
   * Method: Reload current page
   */
  public async reloadPage(): Promise<void> {
    const currentUrl = this.router.url;
    await this.router.navigateByUrl(Constants.SLASH, {skipLocationChange: true});
    await this.router.navigate([currentUrl]);
  }

}
